import { useConstant } from 'common-hooks';
import { getUuid } from 'common-utils';
import { AnimationItem } from 'lottie-web';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { styled } from '../stitches.config';

const StyleClean = styled('div', {
  stroke: 'initial',
  fill: 'initial',
  color: 'initial',
});
export type ControlledLottieProps<T> = {
  height?: number | string;
  width?: number | string;
  path?: string;
  speed?: number;
  animationData?: unknown;
  viewBoxSize?: string;
  state: T;
  loop?: boolean;
  stateFrames: { state: T; segment: [number, number] }[];
} & React.HTMLAttributes<HTMLDivElement>;

export function ControlledLottie<T>(props: ControlledLottieProps<T>) {
  const {
    height = '100%',
    width = '100%',
    animationData,
    path,
    state,
    stateFrames,
    viewBoxSize,
    speed,
    loop = false,
    ...divProps
  } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [controls, setControls] = useState<AnimationItem | null>(null);
  const [lottie, setLottie] = useState<
    typeof import('lottie-web').default | null
  >(null);
  const id = useConstant(() => getUuid());

  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('lottie-web').then((lottieModule) => {
        setLottie(lottieModule.default);
      });
    }
  }, []);

  useEffect(() => {
    if (ref.current && lottie) {
      const controls = lottie.loadAnimation({
        name: id,
        path,
        animationData,
        container: ref.current,
        autoplay: false,
        loop: loop,
        rendererSettings: {
          viewBoxSize,
          preserveAspectRatio: 'xMidYMid slice',
        },
      });
      if (speed) {
        controls.setSpeed(speed);
      }
      setControls(controls);
      const currentFrame = stateFrames.find((frame) => frame.state === state);
      if (currentFrame) {
        controls.goToAndStop(currentFrame.segment[1]);
      }
      return () => {
        controls.destroy();
        setControls(null);
      };
    }
  }, [ref, lottie]);

  useEffect(() => {
    if (controls) {
      const currentFrame = stateFrames.find((frame) => frame.state === state);
      if (currentFrame) {
        controls.playSegments(currentFrame.segment, true);
      }
    }
  }, [state, controls]);

  return useMemo(
    () => (
      <StyleClean
        key={'same-element'}
        {...divProps}
        style={{ width: width, height: height }}
        ref={ref}
      />
    ),
    [],
  );
}
